import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useRef, useState } from 'react';
import { ModalWrapper, SectionTitle } from '../../shared/components';
import { errorMessage, isOverCapacity } from '../tasks.utils';

/**
 * Runs a planning call; when the server refuses it for going over someone's capacity, asks
 * the manager to confirm and retries with `allowOverCapacity: true`.
 */
export function useCapacityConfirm() {
  const [message, setMessage] = useState<string | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const run = useCallback(async <T,>(call: (allowOverCapacity: boolean) => Promise<T>): Promise<T> => {
    try {
      return await call(false);
    } catch (e) {
      if (!isOverCapacity(e)) throw e;
      const ok = await new Promise<boolean>((resolve) => {
        resolver.current = resolve;
        setMessage(errorMessage(e));
      });
      if (!ok) throw Object.assign(new Error('Cancelled'), { cancelled: true });
      return call(true);
    }
  }, []);

  const close = (ok: boolean) => {
    setMessage(null);
    resolver.current?.(ok);
    resolver.current = null;
  };

  const dialog = (
    <ModalWrapper open={message !== null} onClose={() => close(false)} maxWidth='sm'>
      <SectionTitle title='Over capacity' />
      <Typography>{message?.replace(/Confirm with allowOverCapacity to plan it anyway\.?/, '')}</Typography>
      <Typography variant='body2' color='text.secondary'>
        Plan it anyway? The employee will show as overloaded on those days.
      </Typography>
      <Stack direction='row' justifyContent='flex-end' gap={1}>
        <Button onClick={() => close(false)}>Cancel</Button>
        <Button variant='contained' color='warning' onClick={() => close(true)}>
          Plan anyway
        </Button>
      </Stack>
    </ModalWrapper>
  );

  return { run, dialog };
}

/** A cancelled confirmation is not an error worth a toast. */
export const wasCancelled = (e: unknown) => Boolean((e as { cancelled?: boolean })?.cancelled);
